import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { faArrowUpRightFromSquare, faHeartPulse, faLinkSlash, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { Badge, Button, Icon, Input, KebabMenu, SearchableSelect, SegmentedControl, useToast } from '../ui';
import OpenFixBadge from './OpenFixBadge';
import { useUser } from '../../context/UserContext';
import type { AiJob } from '../../lib/aiJobs';
import { addHostedSite, linkSearch, setSiteAccount, sitePlatform, type HostedSite, type HostingAccount } from '../../lib/hosting';
import { fetchOpenFixJobsBySite } from '../../lib/hostingFixPlan';
import { enqueueHostingSetup, fetchOpenSetupJobsBySite } from '../../lib/hostingSetup';

// While a site waits for the runner to find its Cloudways app or Netlify site (its watch loop
// looks every two minutes), re-read the sites this often, for at most this long.
const LINK_POLL_MS = 20_000;
const LINK_POLL_FOR_MS = 10 * 60_000;

const host = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');

type AddMode = 'monitor' | 'new';

export interface HostedSitesEditorProps {
  account: HostingAccount;
  /** Every uptime-monitor site: the account's own, and the unattached ones it can take. */
  allSites: HostedSite[];
  /** Re-read the sites after a change, and while one is still being linked. */
  onChanged: () => Promise<void> | void;
  /** Extra figure per site, right-aligned before its menu (the detail page puts uptime there). */
  aside?: (site: HostedSite) => React.ReactNode;
}

/**
 * An account's websites, one row each: where the runner reaches it (Cloudways, Netlify, or
 * still being linked), what setup made of it, any open agent job, and its actions. Below, Add
 * website attaches an unattached uptime-monitor site or adds a new URL. The account detail page
 * and the Edit drawer both render this, so there is one implementation.
 */
export default function HostedSitesEditor({ account, allSites, onChanged, aside }: HostedSitesEditorProps) {
  const sites = allSites.filter((s) => s.hosting_account_id === account.id);
  const unattached = allSites.filter((s) => !s.hosting_account_id);

  const { user } = useUser();
  const toast = useToast();
  const navigate = useNavigate();

  const [fixJobs, setFixJobs] = useState<Record<string, AiJob>>({});
  const [setupJobs, setSetupJobs] = useState<Record<string, AiJob>>({});
  const loadJobs = useCallback(() => {
    // Badges only: the list works without them.
    fetchOpenFixJobsBySite()
      .then(setFixJobs)
      .catch((err) => console.error('Error loading open hosting fixes:', err));
    fetchOpenSetupJobsBySite()
      .then(setSetupJobs)
      .catch((err) => console.error('Error loading open hosting setups:', err));
  }, []);
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDetach, setConfirmDetach] = useState<string | null>(null);

  const [adding, setAdding] = useState(sites.length === 0);
  const [mode, setMode] = useState<AddMode>(unattached.length ? 'monitor' : 'new');
  const [attachId, setAttachId] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [newName, setNewName] = useState('');

  const act = async (key: string, fn: () => Promise<unknown>, done?: string, detail?: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      if (done) toast.success(done, detail);
      await onChanged();
      return true;
    } catch (err) {
      console.error('Error updating hosted site:', err);
      setError(err instanceof Error ? err.message : 'Failed to update the site');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const attach = async () => {
    const site = unattached.find((s) => s.id === attachId);
    if (!site) return;
    const ok = await act('attach', () => setSiteAccount(site.id, account.id), `${host(site.url)} attached`, linkedNote(site));
    if (ok) {
      setAttachId(null);
      setAdding(false);
    }
  };

  const add = async () => {
    const url = newUrl.trim();
    const ok = await act('add', () => addHostedSite(account.id, newName, url), `${host(url.replace(/^https?:\/\//i, ''))} added`, 'In the uptime monitor now. The runner looks for its Netlify or Cloudways site within a couple of minutes.');
    if (ok) {
      setNewUrl('');
      setNewName('');
      setAdding(false);
    }
  };

  const detach = (site: HostedSite) =>
    act(`detach:${site.id}`, () => setSiteAccount(site.id, null), `${host(site.url)} detached`, 'It stays in the uptime monitor.').then(() => setConfirmDetach(null));

  const setUp = async (site: HostedSite) => {
    if (!user) return;
    const ok = await act(
      `setup:${site.id}`,
      () => enqueueHostingSetup({ account, site, createdBy: { id: user.id, name: user.name || user.email } }),
      'Setup queued',
      `${host(site.url)} is in the agent queue. Its type and checks are set when the job is approved.`,
    );
    if (ok) loadJobs();
  };

  // A just-attached site links on the runner's next pass; keep re-reading until it does.
  const anyLooking = sites.some((s) => linkSearch(s) === 'looking');
  const refresh = useRef(onChanged);
  useEffect(() => {
    refresh.current = onChanged;
  }, [onChanged]);
  useEffect(() => {
    if (!anyLooking) return;
    const since = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - since > LINK_POLL_FOR_MS) window.clearInterval(timer);
      else refresh.current();
    }, LINK_POLL_MS);
    return () => window.clearInterval(timer);
  }, [anyLooking]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {error ? <ErrorNote text={error} /> : null}

      {sites.length ? (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
          {sites.map((s, i) => (
            <SiteRow
              key={s.id}
              site={s}
              first={i === 0}
              fixJob={fixJobs[s.id]}
              setupJob={setupJobs[s.id]}
              aside={aside?.(s)}
              busy={busy}
              confirming={confirmDetach === s.id}
              canSetUp={Boolean(user)}
              onSetUp={() => setUp(s)}
              onAskDetach={() => setConfirmDetach(s.id)}
              onDetach={() => detach(s)}
              onKeep={() => setConfirmDetach(null)}
              onUptime={() => navigate(`/uptime/${s.id}`)}
            />
          ))}
        </ul>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
          No websites yet. Add one and it is watched by the uptime monitor and checked by every run.
        </p>
      )}

      {adding ? (
        <div
          style={{
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            background: 'var(--surface-raised, transparent)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
            <SegmentedControl
              options={[
                { id: 'monitor', label: unattached.length ? `From uptime monitor · ${unattached.length}` : 'From uptime monitor' },
                { id: 'new', label: 'New URL' },
              ]}
              value={mode}
              onChange={(id) => setMode(id as AddMode)}
            />
            {sites.length ? (
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)} disabled={busy !== null}>
                Cancel
              </Button>
            ) : null}
          </div>

          {mode === 'monitor' ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SearchableSelect
                  placeholder={unattached.length ? 'Pick a monitored site…' : 'Every monitored site is attached'}
                  searchPlaceholder="Search sites…"
                  options={unattached.map((s) => ({ value: s.id, label: s.name, hint: host(s.url) }))}
                  value={attachId}
                  onChange={setAttachId}
                />
              </div>
              <Button size="md" variant="secondary" disabled={!attachId || busy !== null} onClick={attach}>
                {busy === 'attach' ? 'Attaching…' : 'Attach'}
              </Button>
            </div>
          ) : (
            /* Stacks on phones: three columns left each field ~120px */
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto]" style={{ gap: '8px', alignItems: 'flex-end' }}>
              <Input label="Website" icon="globe" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="example.com" />
              <Input label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Optional" />
              <span className="justify-self-start sm:justify-self-auto" style={{ display: 'inline-flex' }}>
                <Button size="md" variant="secondary" icon="plus" disabled={!newUrl.trim() || busy !== null} onClick={add}>
                  {busy === 'add' ? 'Adding…' : 'Add'}
                </Button>
              </span>
            </div>
          )}
          <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)', lineHeight: 'var(--leading-snug)' }}>
            {mode === 'monitor'
              ? 'Sites already in the uptime monitor that no hosting account has. Attaching keeps its uptime history.'
              : 'Goes straight into the uptime monitor with the default alert recipients.'}{' '}
            The runner then links it to its Netlify site or Cloudways app by domain.
          </p>
        </div>
      ) : (
        <span>
          <Button size="sm" variant="secondary" icon="plus" onClick={() => setAdding(true)}>
            Add website
          </Button>
        </span>
      )}
    </div>
  );
}

/** What the attach toast says about linking: an attached site may already know its platform. */
const linkedNote = (s: HostedSite) => {
  const p = sitePlatform(s);
  return p ? `Already linked to ${p}, so the next run checks it.` : 'The runner looks for its Netlify or Cloudways site within a couple of minutes.';
};

function SiteRow({
  site: s,
  first,
  fixJob,
  setupJob,
  aside,
  busy,
  confirming,
  canSetUp,
  onSetUp,
  onAskDetach,
  onDetach,
  onKeep,
  onUptime,
}: {
  site: HostedSite;
  first: boolean;
  fixJob: AiJob | undefined;
  setupJob: AiJob | undefined;
  aside: React.ReactNode;
  busy: string | null;
  confirming: boolean;
  canSetUp: boolean;
  onSetUp: () => void;
  onAskDetach: () => void;
  onDetach: () => void;
  onKeep: () => void;
  onUptime: () => void;
}) {
  const platform = sitePlatform(s);
  const search = linkSearch(s);
  const needsSetup = !s.site_kind && !setupJob;
  const named = s.name && s.name.toLowerCase() !== host(s.url).toLowerCase() && s.name.toLowerCase() !== host(s.url).replace(/^www\./i, '').toLowerCase();

  return (
    <li style={{ borderTop: first ? 'none' : '1px solid var(--border-hairline)', padding: '11px 0', display: 'flex', flexDirection: 'column', gap: '7px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <Icon name="globe" size={13} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover-underline"
              title={`Open ${host(s.url)}`}
              style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {host(s.url)}
            </a>
          </span>
          {named ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', paddingLeft: '19px', overflowWrap: 'anywhere' }}>{s.name}</span> : null}
        </div>
        {confirming ? (
          <span style={{ display: 'inline-flex', gap: '6px', flexShrink: 0 }}>
            <Button size="sm" variant="danger" onClick={onDetach} disabled={busy !== null}>
              {busy === `detach:${s.id}` ? 'Detaching…' : 'Detach'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onKeep} disabled={busy !== null}>
              Keep
            </Button>
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {aside}
            <KebabMenu
              label={`Actions for ${host(s.url)}`}
              actions={[
                { label: 'Open uptime', icon: faHeartPulse, onClick: onUptime },
                { label: 'Visit site', icon: faArrowUpRightFromSquare, onClick: () => window.open(s.url, '_blank', 'noopener,noreferrer') },
                needsSetup && { label: 'Set up hosting', icon: faWandMagicSparkles, disabled: !canSetUp || busy !== null, onClick: onSetUp },
                { label: 'Detach from account', icon: faLinkSlash, danger: true, onClick: onAskDetach },
              ]}
            />
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingLeft: '19px' }}>
        {platform ? (
          <Badge tone="neutral" title={platform === 'Cloudways' ? `Cloudways app ${s.cloudways_app_id}` : `Netlify site ${s.netlify_site_id}`}>
            {platform}
          </Badge>
        ) : search === 'looking' ? (
          <Badge tone="info" dot pulse>
            Linking
          </Badge>
        ) : (
          <Badge tone="warn" dot>
            Not linked
          </Badge>
        )}
        {s.site_kind ? (
          <Badge tone="neutral">{s.site_kind === 'app' ? 'App' : 'Marketing site'}</Badge>
        ) : setupJob ? (
          <OpenFixBadge job={setupJob} label="Setup" tone="info" title="Hosting setup in the agent queue" />
        ) : (
          <Badge tone="idle">Not set up</Badge>
        )}
        <OpenFixBadge job={fixJob} />
        {needsSetup ? (
          <button
            type="button"
            className="hover-underline"
            onClick={onSetUp}
            disabled={!canSetUp || busy !== null}
            style={{ border: 0, padding: 0, background: 'transparent', color: 'var(--accent-text)', fontSize: 'var(--text-xs)', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {busy === `setup:${s.id}` ? 'Queueing setup…' : 'Set up hosting'}
          </button>
        ) : null}
      </div>

      {platform ? null : (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', paddingLeft: '19px', lineHeight: 'var(--leading-snug)' }}>
          {search === 'looking'
            ? 'Looking for its Netlify or Cloudways site…'
            : 'Not linked — no Netlify or Cloudways site answers at this domain. Runs skip it until one does.'}
        </span>
      )}
      {confirming ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', paddingLeft: '19px' }}>
          Detach {host(s.url)}? It stays in the uptime monitor and its <Link to={`/uptime/${s.id}`} className="hover-underline" style={{ color: 'var(--accent-text)' }}>history</Link>, but runs stop checking it.
        </span>
      ) : null}
    </li>
  );
}

function ErrorNote({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--status-down-soft)',
        color: 'var(--status-down)',
        fontSize: 'var(--text-sm)',
      }}
    >
      {text}
    </div>
  );
}
